import config from "../../../config/config";
import { authFetch, buildAuthHeaders } from "../../../public/api/httpClient";
import { filterActiveMasterRows } from "../../memberships/utils/masterLookupUtils";

const CHEQUES_API_URL = `${config.BASE_URL}/cheques`;


export const getCheques = async (filters = {}) => {
  const params = new URLSearchParams();

  if (filters.page) params.append("page", filters.page);
  if (filters.limit) params.append("limit", filters.limit);
  if (filters.cheque_no) params.append("cheque_no", filters.cheque_no);
  if (filters.bank_id) params.append("bank_id", filters.bank_id);
  if (filters.cheque_status_id)
    params.append("cheque_status_id", filters.cheque_status_id);
  if (filters.cheque_type_id)
    params.append("cheque_type_id", filters.cheque_type_id);
  if (filters.start_date) params.append("start_date", filters.start_date);
  if (filters.end_date) params.append("end_date", filters.end_date);

  const q = params.toString();

  const res = await authFetch(`${CHEQUES_API_URL}${q ? `?${q}` : ""}`, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });

  const data = await res.json();
  if (!res.ok) throw new Error(data.message || "Error fetching cheques");

  return data;
};

export const getChequeById = async (id) => {
  const res = await authFetch(`${CHEQUES_API_URL}/${id}`, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });

  const data = await res.json();
  if (!res.ok)
    throw new Error(data.message || "Error fetching cheque details");

  return data;
};

function buildChequeFormData(chequeData, signatureFile) {
  const fd = new FormData();
  Object.entries(chequeData || {}).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (value === "" && key !== "remarks") return;
    fd.append(key, value);
  });
  if (chequeData?.cheque_cleared !== undefined) {
    fd.append("cheque_cleared", chequeData.cheque_cleared ? "1" : "0");
  }
  if (signatureFile) {
    fd.append("signature_file", signatureFile);
  }
  return fd;
}

export const createCheque = async (chequeData, csrfToken, signatureFile = null) => {
  try {
    const isFormData = Boolean(signatureFile);
    const body = isFormData
      ? buildChequeFormData(chequeData, signatureFile)
      : JSON.stringify(chequeData);
    const headers = buildAuthHeaders(
      csrfToken ? { "X-CSRF-Token": csrfToken } : {},
    );
    if (isFormData) {
      delete headers["Content-Type"];
    }

    const res = await authFetch(`${CHEQUES_API_URL}/create`, {
      method: "POST",
      credentials: "include",
      headers,
      body,
    });

    const payload = await res.json();

    if (!res.ok) {
      return {
        data: null,
        error: {
          message: payload.message || "Error creating cheque entry",
          details: payload.details,
        },
      };
    }

    return { data: payload, error: null };
  } catch (error) {
    return {
      data: null,
      error: { message: error.message || "Error creating cheque entry" },
    };
  }
};

export const editCheque = async (id, chequeData, csrfToken, signatureFile = null) => {
  const isFormData = Boolean(signatureFile);
  const body = isFormData
    ? buildChequeFormData(chequeData, signatureFile)
    : JSON.stringify(chequeData);
  const headers = buildAuthHeaders(
    csrfToken ? { "X-CSRF-Token": csrfToken } : {},
  );
  if (isFormData) {
    delete headers["Content-Type"];
  }

  const res = await authFetch(`${CHEQUES_API_URL}/${id}`, {
    method: "PUT",
    credentials: "include",
    headers,
    body,
  });

  const payload = await res.json();
  if (!res.ok) throw new Error(payload.message || "Error editing cheque");

  return payload;
};

export const updateChequeStatus = async (id, statusId, clearanceDate, csrfToken) => {
  const body = { cheque_status_id: statusId };
  if (clearanceDate) body.clearance_date = clearanceDate;

  const res = await authFetch(`${CHEQUES_API_URL}/${id}/status`, {
    method: "PATCH",
    credentials: "include",
    headers: buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
    body: JSON.stringify(body),
  });

  const payload = await res.json();
  if (!res.ok)
    throw new Error(payload.message || "Error updating cheque status");

  return payload;
};

export const getChequeMasterData = async () => {
  const res = await authFetch(`${CHEQUES_API_URL}/masters`, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });

  const data = await res.json();
  if (!res.ok)
    throw new Error(data.message || "Error fetching master data");

  const raw = data?.data && typeof data.data === "object" ? data.data : data;
  return {
    banks: filterActiveMasterRows(raw?.banks),
    types: filterActiveMasterRows(raw?.types),
    statuses: filterActiveMasterRows(raw?.statuses),
  };
};

export const searchUsers = async (query) => {
  const params = new URLSearchParams();
  if (query) params.append("q", query);

  const res = await authFetch(
    `${CHEQUES_API_URL}/users/search?${params.toString()}`,
    {
      credentials: "include",
      headers: buildAuthHeaders(),
    },
  );

  const data = await res.json();
  if (!res.ok)
    throw new Error(data.message || "Error searching users");

  return data;
};

export const getChequeStats = async () => {
  const res = await authFetch(`${CHEQUES_API_URL}/stats`, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });

  const data = await res.json();
  if (!res.ok)
    throw new Error(data.message || "Error fetching cheque stats");

  return data;
};
